import { supabase } from './supabase/client';
import { Transaction } from '@flows.cash/types';

export interface DuplicateDetectionCriteria {
  dateWindow: number; // Days to check around the transaction date
  checkAmount: boolean;
  checkAccount: boolean;
  checkDescription: boolean;
  checkPayee: boolean;
  descriptionSimilarityThreshold: number; // 0-1, how similar descriptions need to be
}

export interface PotentialDuplicate {
  existingTransaction: Transaction;
  matchReasons: string[];
  confidence: number; // 0-1, confidence that this is a duplicate
}

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  potentialDuplicates: PotentialDuplicate[];
  newTransaction: Partial<Transaction>;
}

// Performance mode options for duplicate checking
export enum DuplicateCheckMode {
  FAST = 'fast',           // Server-side bulk checking, fastest
  BALANCED = 'balanced',   // Optimized client-side with reduced criteria
  THOROUGH = 'thorough'    // Full client-side checking with all criteria
}

// Default criteria for duplicate detection
export const DEFAULT_DUPLICATE_CRITERIA: DuplicateDetectionCriteria = {
  dateWindow: 1, // Check ±1 day
  checkAmount: true,
  checkAccount: true,
  checkDescription: true,
  checkPayee: false, // Optional since payee might be empty
  descriptionSimilarityThreshold: 0.8
};

// More efficient duplicate detection - select only required fields
export const OPTIMIZED_DUPLICATE_CRITERIA: DuplicateDetectionCriteria = {
  dateWindow: 0, // Same day only (faster)
  checkAmount: true,
  checkAccount: true,
  checkDescription: false, // Disable expensive string similarity
  checkPayee: false,
  descriptionSimilarityThreshold: 0.9 // Higher threshold if enabled
};

// Ultra-fast criteria for large imports
export const FAST_DUPLICATE_CRITERIA: DuplicateDetectionCriteria = {
  dateWindow: 0, // Same day only
  checkAmount: true,
  checkAccount: true,
  checkDescription: false,
  checkPayee: false,
  descriptionSimilarityThreshold: 0.95
};

/**
 * Calculate similarity between two strings using Levenshtein distance
 */
function calculateStringSimilarity(str1: string, str2: string): number {
  if (!str1 || !str2) return 0;
  
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 1;
  
  const len1 = s1.length;
  const len2 = s2.length;
  
  if (len1 === 0) return len2 === 0 ? 1 : 0;
  if (len2 === 0) return 0;
  
  // Create matrix
  const matrix: number[][] = [];
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }
  
  // Fill matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,     // deletion
        matrix[i][j - 1] + 1,     // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }
  
  const maxLen = Math.max(len1, len2);
  return 1 - (matrix[len1][len2] / maxLen);
}

/**
 * OPTIMIZED: Server-side bulk duplicate checking using SQL function
 * This is significantly faster for large batches (1000+ transactions)
 */
export async function checkBatchForDuplicatesOptimized(
  userId: string,
  transactions: Partial<Transaction>[],
  criteria: DuplicateDetectionCriteria = FAST_DUPLICATE_CRITERIA
): Promise<Array<DuplicateCheckResult>> {
  try {
    console.log(`🚀 Starting optimized bulk duplicate check for ${transactions.length} transactions`);
    const startTime = Date.now();
    
    // Prepare transactions for the SQL function
    const transactionsJson = transactions.map(tx => ({
      date: tx.date,
      amount: tx.amount,
      account_id: tx.account_id,
      description: tx.description || '',
      payee_id: tx.payee_id || null
    }));
    
    // Call the server-side function
    console.log('📡 Calling server-side bulk_check_duplicates function...');
    const { data, error } = await supabase.rpc('bulk_check_duplicates', {
      p_user_id: userId,
      p_transactions: JSON.stringify(transactionsJson),
      p_date_window_days: criteria.dateWindow,
      p_check_description: criteria.checkDescription,
      p_description_threshold: criteria.descriptionSimilarityThreshold
    });
    
    if (error) {
      console.error('❌ Server-side duplicate check failed:', error);
      // Fallback to client-side checking
      console.log('🔄 Falling back to optimized client-side checking...');
      return await checkBatchForDuplicates(userId, transactions, OPTIMIZED_DUPLICATE_CRITERIA);
    }
    
    if (!data || !Array.isArray(data)) {
      console.warn('⚠️ Invalid response from server-side duplicate check, falling back');
      return await checkBatchForDuplicates(userId, transactions, OPTIMIZED_DUPLICATE_CRITERIA);
    }
    
    console.log(`✅ Server-side duplicate check completed in ${Date.now() - startTime}ms`);
    
    // Transform results to match the expected format
    const results: DuplicateCheckResult[] = transactions.map((transaction, index) => {
      const serverResult = data.find((r: any) => r.transaction_index === index);
      
      if (!serverResult) {
        return {
          isDuplicate: false,
          potentialDuplicates: [],
          newTransaction: transaction
        };
      }
      
      // Convert server response to expected format
      const potentialDuplicates: PotentialDuplicate[] = [];
      if (serverResult.is_duplicate && serverResult.existing_transaction_ids?.length > 0) {
        // For now, we'll create a simplified potential duplicate
        // In a real implementation, you might want to fetch the full transaction details
        potentialDuplicates.push({
          existingTransaction: {
            id: serverResult.existing_transaction_ids[0],
            // Add other fields as needed - for performance we use minimal data
          } as Transaction,
          matchReasons: serverResult.match_reasons || ['Server-side match'],
          confidence: serverResult.confidence_score || 0.8
        });
      }
      
      return {
        isDuplicate: serverResult.is_duplicate || false,
        potentialDuplicates,
        newTransaction: transaction
      };
    });
    
    const duplicateCount = results.filter(r => r.isDuplicate).length;
    console.log(`📊 Found ${duplicateCount} potential duplicates out of ${transactions.length} transactions`);
    
    return results;
    
  } catch (error) {
    console.error('💥 Exception in optimized duplicate check:', error);
    // Fallback to client-side checking
    console.log('🔄 Falling back to client-side duplicate checking...');
    return await checkBatchForDuplicates(userId, transactions, OPTIMIZED_DUPLICATE_CRITERIA);
  }
}

/**
 * Smart duplicate checking that chooses the best method based on batch size
 */
export async function checkBatchForDuplicatesSmart(
  userId: string,
  transactions: Partial<Transaction>[],
  mode: DuplicateCheckMode = DuplicateCheckMode.FAST,
  criteria?: DuplicateDetectionCriteria
): Promise<Array<DuplicateCheckResult>> {
  const batchSize = transactions.length;
  
  // Choose the best approach based on batch size and mode
  if (mode === DuplicateCheckMode.FAST && batchSize >= 100) {
    console.log(`🎯 Using server-side bulk checking for ${batchSize} transactions (FAST mode)`);
    return await checkBatchForDuplicatesOptimized(userId, transactions, criteria || FAST_DUPLICATE_CRITERIA);
  } else if (mode === DuplicateCheckMode.BALANCED && batchSize >= 50) {
    console.log(`⚖️ Using optimized client-side checking for ${batchSize} transactions (BALANCED mode)`);
    return await checkBatchForDuplicates(userId, transactions, criteria || OPTIMIZED_DUPLICATE_CRITERIA);
  } else {
    console.log(`🔍 Using thorough client-side checking for ${batchSize} transactions (THOROUGH mode)`);
    return await checkBatchForDuplicates(userId, transactions, criteria || DEFAULT_DUPLICATE_CRITERIA);
  }
}

/**
 * Check if a new transaction is potentially a duplicate of existing transactions
 */
export async function checkForDuplicates(
  userId: string,
  newTransaction: Partial<Transaction>,
  criteria: DuplicateDetectionCriteria = DEFAULT_DUPLICATE_CRITERIA
): Promise<DuplicateCheckResult> {
  try {
    if (!newTransaction.date || !newTransaction.amount || !newTransaction.account_id) {
      return {
        isDuplicate: false,
        potentialDuplicates: [],
        newTransaction
      };
    }

    // Calculate date range for checking
    const transactionDate = new Date(newTransaction.date);
    const startDate = new Date(transactionDate);
    startDate.setDate(startDate.getDate() - criteria.dateWindow);
    const endDate = new Date(transactionDate);
    endDate.setDate(endDate.getDate() + criteria.dateWindow);

    // Build query to find potential duplicates
    let query = supabase
      .from('transactions')
      .select('*')
      .eq('user_id', userId)
      .gte('date', startDate.toISOString())
      .lte('date', endDate.toISOString());

    // Add amount filter if enabled
    if (criteria.checkAmount) {
      query = query.eq('amount', newTransaction.amount);
    }

    // Add account filter if enabled
    if (criteria.checkAccount) {
      query = query.eq('account_id', newTransaction.account_id);
    }

    const { data: existingTransactions, error } = await query;

    if (error) {
      console.error('Error checking for duplicates:', error);
      throw error;
    }

    if (!existingTransactions || existingTransactions.length === 0) {
      return {
        isDuplicate: false,
        potentialDuplicates: [],
        newTransaction
      };
    }

    // Analyze potential duplicates
    const potentialDuplicates: PotentialDuplicate[] = [];

    for (const existing of existingTransactions) {
      const matchReasons: string[] = [];
      let confidence = 0;
      let matches = 0;
      let totalChecks = 0;

      // Check amount match
      if (criteria.checkAmount) {
        totalChecks++;
        if (Math.abs(existing.amount - (newTransaction.amount || 0)) < 0.01) {
          matches++;
          matchReasons.push('Same amount');
        }
      }

      // Check account match
      if (criteria.checkAccount) {
        totalChecks++;
        if (existing.account_id === newTransaction.account_id) {
          matches++;
          matchReasons.push('Same account');
        }
      }

      // Check date proximity
      totalChecks++;
      const existingDate = new Date(existing.date);
      const daysDiff = Math.abs(
        (existingDate.getTime() - transactionDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysDiff <= criteria.dateWindow) {
        matches++;
        if (daysDiff === 0) {
          matchReasons.push('Same date');
        } else {
          matchReasons.push(`Similar date (${daysDiff.toFixed(1)} days apart)`);
        }
      }

      // Check description similarity
      if (criteria.checkDescription && newTransaction.description && existing.description) {
        totalChecks++;
        const similarity = calculateStringSimilarity(
          newTransaction.description,
          existing.description
        );
        if (similarity >= criteria.descriptionSimilarityThreshold) {
          matches++;
          if (similarity === 1) {
            matchReasons.push('Identical description');
          } else {
            matchReasons.push(`Similar description (${Math.round(similarity * 100)}% match)`);
          }
        }
      }

      // Check payee match (if both have payees)
      if (criteria.checkPayee && newTransaction.payee_id && existing.payee_id) {
        totalChecks++;
        if (existing.payee_id === newTransaction.payee_id) {
          matches++;
          matchReasons.push('Same payee');
        }
      }

      // Calculate confidence based on matches
      confidence = totalChecks > 0 ? matches / totalChecks : 0;

      // Consider it a potential duplicate if confidence is high enough
      if (confidence >= 0.6 && matchReasons.length >= 2) {
        potentialDuplicates.push({
          existingTransaction: existing as Transaction,
          matchReasons,
          confidence
        });
      }
    }

    // Sort by confidence (highest first)
    potentialDuplicates.sort((a, b) => b.confidence - a.confidence);

    return {
      isDuplicate: potentialDuplicates.length > 0,
      potentialDuplicates,
      newTransaction
    };
  } catch (error) {
    console.error('Error in duplicate detection:', error);
    throw error;
  }
}

/**
 * Optimized duplicate check - faster but less comprehensive
 */
export async function checkForDuplicatesOptimized(
  userId: string,
  newTransaction: Partial<Transaction>,
  criteria: DuplicateDetectionCriteria = OPTIMIZED_DUPLICATE_CRITERIA
): Promise<DuplicateCheckResult> {
  try {
    if (!newTransaction.date || !newTransaction.amount || !newTransaction.account_id) {
      return {
        isDuplicate: false,
        potentialDuplicates: [],
        newTransaction
      };
    }

    // Same day only for faster queries
    const transactionDate = new Date(newTransaction.date);
    const startOfDay = new Date(transactionDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(transactionDate);
    endOfDay.setHours(23, 59, 59, 999);

    // Select only required fields for better performance
    let query = supabase
      .from('transactions')
      .select('id, amount, date, account_id, description, payee_id')
      .eq('user_id', userId)
      .eq('amount', newTransaction.amount)
      .eq('account_id', newTransaction.account_id)
      .gte('date', startOfDay.toISOString())
      .lte('date', endOfDay.toISOString())
      .limit(5); // Limit results for better performance

    const { data: existingTransactions, error } = await query;

    if (error) {
      console.error('Error checking for duplicates:', error);
      throw error;
    }

    if (!existingTransactions || existingTransactions.length === 0) {
      return {
        isDuplicate: false,
        potentialDuplicates: [],
        newTransaction
      };
    }

    // Simple exact match logic (no expensive string similarity)
    const potentialDuplicates: PotentialDuplicate[] = [];
    
    for (const existing of existingTransactions) {
      const matchReasons: string[] = [];
      
      // Exact matches only
      if (Math.abs(existing.amount - (newTransaction.amount || 0)) < 0.01) {
        matchReasons.push('Same amount');
      }
      
      if (existing.account_id === newTransaction.account_id) {
        matchReasons.push('Same account');
      }
      
      // Same day check
      const existingDate = new Date(existing.date);
      if (existingDate.toDateString() === transactionDate.toDateString()) {
        matchReasons.push('Same date');
      }
      
      // Consider duplicate if 3+ exact matches
      if (matchReasons.length >= 3) {
        potentialDuplicates.push({
          existingTransaction: existing as Transaction,
          matchReasons,
          confidence: 0.9 // High confidence for exact matches
        });
      }
    }

    return {
      isDuplicate: potentialDuplicates.length > 0,
      potentialDuplicates,
      newTransaction
    };
  } catch (error) {
    console.error('Error in optimized duplicate detection:', error);
    throw error;
  }
}

/**
 * Check for duplicates in a batch of transactions (for import) - ORIGINAL CLIENT-SIDE METHOD
 * This is kept for backward compatibility and as a fallback
 */
export async function checkBatchForDuplicates(
  userId: string,
  transactions: Partial<Transaction>[],
  criteria: DuplicateDetectionCriteria = DEFAULT_DUPLICATE_CRITERIA
): Promise<Array<DuplicateCheckResult>> {
  const results: DuplicateCheckResult[] = [];
  
  console.log(`🔄 Processing ${transactions.length} transactions with client-side duplicate checking...`);
  
  // Process in smaller chunks to avoid overwhelming the client
  const CHUNK_SIZE = 50;
  for (let i = 0; i < transactions.length; i += CHUNK_SIZE) {
    const chunk = transactions.slice(i, Math.min(i + CHUNK_SIZE, transactions.length));
    
    // Process chunk in parallel for better performance
    const chunkPromises = chunk.map(transaction => 
      checkForDuplicates(userId, transaction, criteria)
    );
    
    const chunkResults = await Promise.all(chunkPromises);
    results.push(...chunkResults);
    
    // Log progress
    console.log(`📈 Processed ${Math.min(i + CHUNK_SIZE, transactions.length)}/${transactions.length} transactions`);
  }
  
  return results;
}

/**
 * Get user's duplicate detection preferences
 */
export async function getUserDuplicatePreferences(userId: string): Promise<DuplicateDetectionCriteria> {
  try {
    const { data, error } = await supabase
      .from('settings')
      .select('duplicate_detection_criteria')
      .eq('user_id', userId)
      .single();

    if (error || !data?.duplicate_detection_criteria) {
      return DEFAULT_DUPLICATE_CRITERIA;
    }

    return { ...DEFAULT_DUPLICATE_CRITERIA, ...data.duplicate_detection_criteria };
  } catch (error) {
    console.error('Error getting duplicate preferences:', error);
    return DEFAULT_DUPLICATE_CRITERIA;
  }
}

/**
 * Save user's duplicate detection preferences
 */
export async function saveUserDuplicatePreferences(
  userId: string,
  criteria: DuplicateDetectionCriteria
): Promise<void> {
  try {
    const { error } = await supabase
      .from('settings')
      .upsert({
        user_id: userId,
        duplicate_detection_criteria: criteria,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error saving duplicate preferences:', error);
    throw error;
  }
} 